import { SlideStatement } from "../../../SlideStatement";
import { SupLink } from "../../../SlideContext";

export default function WhatIsAso() {
  return (
    <div className="flex w-full flex-col px-6 pt-8 pb-4 text-slate-900 md:h-full md:px-24 md:pt-16 md:pb-8 md:[&_header]:mb-0">
      <SlideStatement eyebrow="ASO">
        <div>
          <h1 className="text-3xl font-semibold leading-[1.05] tracking-tight md:text-7xl">
            ASO = App Store Optimization
          </h1>
          {/* This line restates the subject rather than supporting it — the
              second half of a definition — so it belongs above the rule with
              the claim. A line that draws a CONSEQUENCE from the claim (see
              slide 4's closing bar) belongs below it. */}
          <p className="mt-4 text-xl font-medium tracking-tight text-slate-600 md:mt-5 md:text-3xl">
            The craft of winning the installs you don't pay for.
          </p>
        </div>
      </SlideStatement>

      {/* Below the rule — the substantiation. */}
      <div className="flex flex-col gap-5 md:min-h-0 md:flex-1 md:gap-5 md:pt-6">
      {/* Ecosystem diagram — one panel, one seam: the seam is the branch.
          Two channel halves meet under a spanning header; the organic half
          is wider (the slide's subject) and the ASO card fills it. */}
      <div className="flex flex-col overflow-hidden rounded-2xl bg-white ring-1 ring-slate-200">
        <div className="border-b border-slate-200 px-5 py-3 text-center md:py-3">
          <p className="text-sm font-bold uppercase tracking-[0.18em] text-slate-700 md:text-base">
            How apps get installs
          </p>
        </div>

        <div className="grid flex-1 grid-cols-1 divide-y divide-slate-200 md:grid-cols-2 md:divide-x md:divide-y-0">
          {/* Paid channel — the gray world: receded, ghosted, inert */}
          <div className="flex flex-col bg-slate-100/80 p-5 md:px-8 md:py-4">
            <div>
              <p className="text-lg font-bold tracking-tight text-slate-500 md:text-2xl">
                Paid Channels
              </p>
              <p className="mt-0.5 text-xs text-slate-500 md:text-sm">
                $109B/yr media spend: $78B ads + $31B remarketing
                <SupLink to="sources">15</SupLink>
              </p>
            </div>
            <ul className="mt-3 mb-1 flex flex-1 flex-col gap-3 md:mt-3 md:gap-2.5">
              <ChannelRow tone="slate" name="Ad networks & DSPs" eg="Meta, Google, TikTok, AppLovin, Moloco, Liftoff" />
              <ChannelRow tone="slate" name="Campaign & bid management" eg="in-house and third-party UA tooling" />
              <ChannelRow tone="slate" name="Attribution / MMPs" eg="AppsFlyer, Adjust" />
              <ChannelRow tone="slate" name="Ad-creative production" eg="studios & gen-AI tools" />
            </ul>
          </div>

          {/* Organic channel — the blue world: tinted, full-contrast, alive */}
          <div className="flex flex-col bg-blue-50/40 p-5 md:px-8 md:py-4">
            <div>
              <p className="text-lg font-bold tracking-tight text-blue-700 md:text-2xl">
                Organic Channels
              </p>
              <p className="mt-0.5 text-xs text-slate-600 md:text-sm">
                Where apps get found without paying per install
              </p>
            </div>
            <ul className="mt-3 mb-1 flex flex-1 flex-col gap-3 md:mt-3 md:gap-2.5">
              <ChannelRow tone="blue" name="Store search" eg="App Store & Google Play" />
              <ChannelRow tone="blue" name="Charts, featuring & ranking lists" />
              <ChannelRow tone="blue" name="AI assistants" eg="ChatGPT, Claude, Gemini, Perplexity" />
              <ChannelRow tone="blue" name="Google & web search" />
            </ul>
          </div>
        </div>

        {/* ASO foundation — full-width layer under BOTH channels; each column
            states what ASO does for the channel directly above it */}
        <div className="bg-blue-600 px-5 py-4 md:px-8 md:py-5">
          <p className="text-center text-lg font-bold tracking-tight text-white md:text-2xl">
            ASO is the discipline underpinning and optimizing both channels
          </p>
          <div className="mt-2.5 grid grid-cols-1 gap-2 md:grid-cols-2 md:gap-10 md:items-start">
            <p className="text-sm leading-snug text-blue-50 md:text-base">
              <span className="font-semibold text-white">
                ↑ Paid efficiency
              </span>{" "}
              — your paid traffic converts on the same product page. A 20% lift
              in store conversion is 20% more installs for the same spend, plus
              better Search Ads relevance and lower cost per tap.
            </p>
            <p className="text-sm leading-snug text-blue-50 md:text-base">
              <span className="font-semibold text-white">
                ↑ Organic reach &amp; conversion
              </span>{" "}
              — more impressions and installs from search, ranking lists, and
              LLM answers, compounding over time.
            </p>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
}

function ChannelRow({
  name,
  eg,
  tone,
}: {
  name: string;
  eg?: string;
  tone: "slate" | "blue";
}) {
  const isBlue = tone === "blue";
  return (
    <li className="flex items-baseline gap-2.5">
      <span
        aria-hidden="true"
        className={`h-1.5 w-1.5 shrink-0 translate-y-[-1px] rounded-full ${
          isBlue ? "bg-blue-500" : "bg-slate-300"
        }`}
      />
      <span className="text-sm leading-snug md:text-base">
        <span
          className={
            isBlue ? "font-medium text-slate-900" : "font-medium text-slate-600"
          }
        >
          {name}
        </span>
        {eg ? (
          <span className={isBlue ? "text-slate-500" : "text-slate-400"}>
            {" "}
            — {eg}
          </span>
        ) : null}
      </span>
    </li>
  );
}
